import React, { useEffect, useMemo, useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { Banknote, BriefcaseBusiness, Save, UserRound } from 'lucide-react';
import { employeesService } from '../../services/hrService';
import type { Employee } from '../../types/firestore';
import { getDefaultEmployeePermissions } from '../../utils/employeePermissions';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import StatusBadge from '../ui/StatusBadge';

interface EditEmployeeModalProps {
  employee: Employee;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type FormData = {
  name: string;
  email: string;
  phone: string;
  employeeId: string;
  role: Employee['role'];
  department: Employee['department'];
  position: string;
  hireDate: string;
  contractType: Employee['employment']['contractType'];
  baseSalary: string;
  payrollType: Employee['salary']['payrollType'];
  address: string;
};

const departments: Array<{ value: Employee['department']; label: string }> = [
  { value: 'management', label: 'الإدارة' },
  { value: 'teaching', label: 'التعليم' },
  { value: 'finance', label: 'المالية' },
  { value: 'hr', label: 'الموارد البشرية' },
  { value: 'customer_service', label: 'خدمة العملاء' },
  { value: 'technical', label: 'تقنية المعلومات' },
  { value: 'administration', label: 'الشؤون الإدارية' },
];

const roles: Array<{ value: Employee['role']; label: string }> = [
  { value: 'employee', label: 'موظف' },
  { value: 'assistant', label: 'مساعد إداري' },
  { value: 'supervisor', label: 'مشرف' },
  { value: 'accountant', label: 'محاسب' },
  { value: 'customer_service', label: 'خدمة عملاء' },
  { value: 'teacher', label: 'مدرس' },
  { value: 'hr_manager', label: 'مسؤول موارد بشرية' },
  { value: 'admin', label: 'مدير' },
];

const toInputDate = (value: unknown) => {
  if (!value) return '';
  try {
    const date =
      typeof (value as { toDate?: () => Date }).toDate === 'function'
        ? (value as { toDate: () => Date }).toDate()
        : new Date(value as string | number | Date);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
  } catch {
    return '';
  }
};

const toTimestamp = (value: string) => {
  const parsed = new Date(value);
  return Timestamp.fromDate(Number.isNaN(parsed.getTime()) ? new Date() : parsed);
};

const buildForm = (employee: Employee): FormData => ({
  name: employee.name || '',
  email: employee.email || '',
  phone: employee.phone || '',
  employeeId: employee.employeeId || '',
  role: employee.role || 'employee',
  department: employee.department || 'administration',
  position: employee.position || '',
  hireDate: toInputDate(employee.employment?.hireDate) || new Date().toISOString().slice(0, 10),
  contractType: employee.employment?.contractType || 'full_time',
  baseSalary: String(employee.salary?.base ?? ''),
  payrollType: employee.salary?.payrollType || 'monthly',
  address: employee.personalInfo?.address || '',
});

const EditEmployeeModal: React.FC<EditEmployeeModalProps> = ({
  employee,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FormData>(() => buildForm(employee));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(buildForm(employee));
      setErrors({});
      setIsSubmitting(false);
    }
  }, [employee, isOpen]);

  const nextPermissions = useMemo(
    () =>
      getDefaultEmployeePermissions(
        formData.role,
        formData.department,
        employee.permissions
      ),
    [employee.permissions, formData.department, formData.role]
  );

  const setField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData(current => ({ ...current, [field]: value }));
    setErrors(current => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    const baseSalary = Number(formData.baseSalary);

    if (!formData.name.trim()) nextErrors.name = 'اسم الموظف مطلوب.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      nextErrors.email = 'أدخل بريدًا إلكترونيًا صحيحًا.';
    }
    if (!formData.phone.trim()) nextErrors.phone = 'رقم الهاتف مطلوب.';
    if (!formData.employeeId.trim()) nextErrors.employeeId = 'رقم الموظف مطلوب.';
    if (!formData.position.trim()) nextErrors.position = 'المسمى الوظيفي مطلوب.';
    if (!formData.hireDate) nextErrors.hireDate = 'تاريخ التعيين مطلوب.';
    if (!formData.baseSalary || Number.isNaN(baseSalary) || baseSalary < 0) {
      nextErrors.baseSalary = 'أدخل راتبًا صحيحًا لا يقل عن صفر.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await employeesService.updateEmployee(employee.id, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        employeeId: formData.employeeId.trim(),
        role: formData.role,
        department: formData.department,
        position: formData.position.trim(),
        salary: {
          ...employee.salary,
          base: Number(formData.baseSalary),
          currency: 'EGP',
          payrollType: formData.payrollType,
        },
        employment: {
          ...employee.employment,
          hireDate: toTimestamp(formData.hireDate),
          contractType: formData.contractType,
        },
        personalInfo: {
          ...employee.personalInfo,
          address: formData.address.trim(),
        },
        permissions: nextPermissions,
      });

      toast.success('تم تحديث بيانات الموظف بنجاح');
      onSuccess();
      onClose();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'تعذر تحديث بيانات الموظف. راجع البيانات وحاول مرة أخرى.';
      setErrors({ general: message });
      toast.error('تعذر تحديث بيانات الموظف');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isSubmitting) onClose();
      }}
      title='تعديل بيانات الموظف'
      description='حدّث البيانات الوظيفية والأساسية مع إبقاء الحساب مرتبطًا بالمؤسسة.'
      size='xl'
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={isSubmitting}>
            إلغاء
          </Button>
          <Button
            type='submit'
            form='edit-employee-form'
            loading={isSubmitting}
            leadingIcon={<Save className='h-4 w-4' />}
          >
            حفظ التعديلات
          </Button>
        </>
      }
    >
      <form id='edit-employee-form' className='space-y-6' onSubmit={handleSubmit}>
        {errors.general ? (
          <div className='rounded-lg border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:border-danger-900 dark:bg-danger-950 dark:text-danger-200'>
            {errors.general}
          </div>
        ) : null}

        <section className='space-y-4'>
          <div className='flex items-center gap-2 text-sm font-bold text-secondary-900 dark:text-white'>
            <UserRound className='h-4 w-4 text-primary-600' />
            البيانات الأساسية
          </div>
          <div className='grid gap-4 md:grid-cols-2'>
            <Input
              label='اسم الموظف'
              value={formData.name}
              onChange={event => setField('name', event.target.value)}
              error={errors.name}
              disabled={isSubmitting}
              required
            />
            <Input
              label='البريد الإلكتروني'
              type='email'
              value={formData.email}
              onChange={event => setField('email', event.target.value)}
              error={errors.email}
              disabled={isSubmitting}
              required
            />
            <Input
              label='الهاتف'
              type='tel'
              value={formData.phone}
              onChange={event => setField('phone', event.target.value)}
              error={errors.phone}
              disabled={isSubmitting}
              required
            />
            <Input
              label='رقم الموظف'
              value={formData.employeeId}
              onChange={event => setField('employeeId', event.target.value)}
              error={errors.employeeId}
              disabled={isSubmitting}
              required
            />
            <Input
              label='العنوان'
              value={formData.address}
              onChange={event => setField('address', event.target.value)}
              disabled={isSubmitting}
              className='md:col-span-2'
            />
          </div>
        </section>

        <section className='space-y-4'>
          <div className='flex items-center gap-2 text-sm font-bold text-secondary-900 dark:text-white'>
            <BriefcaseBusiness className='h-4 w-4 text-primary-600' />
            الوظيفة والصلاحيات
          </div>
          <div className='grid gap-4 md:grid-cols-2'>
            <SelectField
              label='القسم'
              value={formData.department}
              options={departments}
              disabled={isSubmitting}
              onChange={value =>
                setField('department', value as Employee['department'])
              }
            />
            <SelectField
              label='الدور'
              value={formData.role}
              options={roles}
              disabled={isSubmitting}
              onChange={value => setField('role', value as Employee['role'])}
            />
            <Input
              label='المسمى الوظيفي'
              value={formData.position}
              onChange={event => setField('position', event.target.value)}
              error={errors.position}
              disabled={isSubmitting}
              required
            />
            <SelectField
              label='نوع العقد'
              value={formData.contractType}
              disabled={isSubmitting}
              onChange={value =>
                setField(
                  'contractType',
                  value as Employee['employment']['contractType']
                )
              }
              options={[
                { value: 'full_time', label: 'دوام كامل' },
                { value: 'part_time', label: 'دوام جزئي' },
                { value: 'contract', label: 'تعاقد' },
                { value: 'intern', label: 'تدريب' },
              ]}
            />
            <Input
              label='تاريخ التعيين'
              type='date'
              value={formData.hireDate}
              onChange={event => setField('hireDate', event.target.value)}
              error={errors.hireDate}
              disabled={isSubmitting}
              required
            />
          </div>
          <div className='flex flex-wrap gap-2 rounded-lg bg-secondary-50 p-3 dark:bg-secondary-800'>
            <StatusBadge tone={nextPermissions.canAccessHR ? 'success' : 'neutral'}>
              {nextPermissions.canAccessHR ? 'له وصول للموارد البشرية' : 'بدون وصول HR'}
            </StatusBadge>
            <StatusBadge tone={nextPermissions.canCreate ? 'success' : 'neutral'}>
              {nextPermissions.canCreate ? 'إنشاء وتعديل' : 'قراءة فقط'}
            </StatusBadge>
            <StatusBadge tone={nextPermissions.canViewFinancials ? 'success' : 'neutral'}>
              {nextPermissions.canViewFinancials ? 'صلاحيات مالية' : 'بدون صلاحيات مالية'}
            </StatusBadge>
          </div>
        </section>

        <section className='space-y-4'>
          <div className='flex items-center gap-2 text-sm font-bold text-secondary-900 dark:text-white'>
            <Banknote className='h-4 w-4 text-primary-600' />
            الراتب
          </div>
          <div className='grid gap-4 md:grid-cols-2'>
            <Input
              label='الراتب الأساسي'
              type='number'
              min='0'
              value={formData.baseSalary}
              onChange={event => setField('baseSalary', event.target.value)}
              error={errors.baseSalary}
              hint='العملة المعتمدة: EGP'
              disabled={isSubmitting}
              required
            />
            <SelectField
              label='نوع الراتب'
              value={formData.payrollType}
              disabled={isSubmitting}
              onChange={value =>
                setField(
                  'payrollType',
                  value as Employee['salary']['payrollType']
                )
              }
              options={[
                { value: 'monthly', label: 'شهري' },
                { value: 'hourly', label: 'بالساعة' },
                { value: 'daily', label: 'يومي' },
              ]}
            />
          </div>
        </section>
      </form>
    </Modal>
  );
};

const SelectField: React.FC<{
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  disabled?: boolean;
  onChange: (value: string) => void;
}> = ({ label, value, options, disabled, onChange }) => (
  <label className='space-y-1.5 text-sm font-semibold text-secondary-700 dark:text-secondary-200'>
    <span>{label}</span>
    <select
      value={value}
      disabled={disabled}
      onChange={event => onChange(event.target.value)}
      className='min-h-11 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm text-secondary-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:cursor-not-allowed disabled:bg-secondary-100 disabled:text-secondary-500 dark:border-secondary-700 dark:bg-secondary-900 dark:text-white dark:disabled:bg-secondary-800'
    >
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

export default EditEmployeeModal;
